import { useEffect, useRef } from "react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";

import { tw } from "~/utils";
import { TableSkeleton } from "./TableSkeleton";

const DEFAULT_ROW_HEIGHT_PX = 80;
const SKELETON_ROW_COUNT = 6;
const REACH_END_THRESHOLD_PX = 200;
const OVERSCAN_ROWS = 8;

type CompactTableVariant = "striped" | "plain";

const variantStyles: Record<
  CompactTableVariant,
  {
    emptyState: string;
    maxHeight: string;
    thead: string;
    headerRow: string;
    th: string;
    row: (index: number) => string;
    td: string;
  }
> = {
  striped: {
    emptyState: "p-4 text-center text-sm text-neutral-600",
    maxHeight: "max-h-80",
    thead: "sticky top-0 z-10",
    headerRow: "bg-soft-pastel-lavender text-xs text-black",
    th: "border-r border-white p-3 font-medium",
    row: (index) =>
      tw(
        "bg-light-lavender-gray",
        index % 2 === 0 && "bg-light-lavender-gray/50",
      ),
    td: "min-h-20 min-w-20 border-r border-white px-3 py-1 align-top text-xs text-neutral-900",
  },
  plain: {
    emptyState: "py-3 text-lg text-neutral-800",
    maxHeight: "",
    thead: "",
    headerRow: "border-light-lavender-gray border-b text-xs text-neutral-900",
    th: "leading-6.5 py-2 pr-4 text-left font-medium uppercase tracking-wider last:pr-0",
    row: () => "border-light-lavender-gray border-b last:border-b-0",
    td: "py-3 pr-4 align-top text-sm text-neutral-800 last:pr-0",
  },
};

type CompactTableProps<T> = {
  data: T[];
  columns: ColumnDef<T>[];
  emptyState?: string;
  isLoading?: boolean;
  maxHeightClassName?: string;
  tdClassName?: string;
  virtualized?: boolean;
  estimateRowHeight?: number;
  onReachEnd?: () => void;
  variant?: CompactTableVariant;
};

export function CompactTable<T>({
  data,
  columns,
  emptyState = "No data available.",
  isLoading = false,
  maxHeightClassName,
  tdClassName = "",
  virtualized = false,
  estimateRowHeight = DEFAULT_ROW_HEIGHT_PX,
  onReachEnd,
  variant = "striped",
}: CompactTableProps<T>) {
  const styles = variantStyles[variant];
  const scrollRef = useRef<HTMLDivElement>(null);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const rows = table.getRowModel().rows;

  const virtualizer = useVirtualizer({
    count: virtualized ? rows.length : 0,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => estimateRowHeight,
    overscan: OVERSCAN_ROWS,
  });

  useEffect(() => {
    const element = scrollRef.current;

    if (!onReachEnd || !element) return;

    if (element.scrollHeight <= element.clientHeight) onReachEnd();
  }, [onReachEnd, rows.length]);

  const virtualRows = virtualizer.getVirtualItems();
  const hasVirtualRows = virtualRows.length > 0;
  const paddingTop = hasVirtualRows ? virtualRows[0].start : 0;
  const paddingBottom = hasVirtualRows
    ? virtualizer.getTotalSize() - virtualRows[virtualRows.length - 1].end
    : 0;

  const visibleRows = virtualized
    ? virtualRows.map((virtualRow) => ({
        row: rows[virtualRow.index],
        index: virtualRow.index,
      }))
    : rows.map((row, index) => ({ row, index }));

  const handleScroll = () => {
    const element = scrollRef.current;

    if (!onReachEnd || !element) return;

    const distanceToEnd =
      element.scrollHeight - element.scrollTop - element.clientHeight;

    if (distanceToEnd <= REACH_END_THRESHOLD_PX) onReachEnd();
  };

  const hasColumnSizes = columns.some((column) => column.size !== undefined);
  const isEmpty = !data || data.length === 0;
  const showSkeleton = isLoading && isEmpty;

  if (isEmpty && !showSkeleton) {
    return <div className={styles.emptyState}>{emptyState}</div>;
  }

  return (
    <div
      ref={scrollRef}
      onScroll={handleScroll}
      className={tw("overflow-auto", maxHeightClassName ?? styles.maxHeight)}
    >
      <table
        className={tw(
          "w-full border-collapse",
          hasColumnSizes && "table-fixed",
        )}
      >
        <thead className={styles.thead}>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className={styles.headerRow}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className={styles.th}
                  style={
                    hasColumnSizes
                      ? { width: header.column.columnDef.size }
                      : undefined
                  }
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody>
          {showSkeleton && (
            <TableSkeleton
              numRows={SKELETON_ROW_COUNT}
              columnsLength={columns.length}
            />
          )}
          {paddingTop > 0 && (
            <tr aria-hidden="true">
              <td
                colSpan={columns.length}
                className="p-0"
                style={{ height: paddingTop }}
              />
            </tr>
          )}
          {visibleRows.map(({ row, index }) => (
            <tr
              key={row.id}
              data-index={virtualized ? index : undefined}
              ref={virtualized ? virtualizer.measureElement : undefined}
              className={styles.row(index)}
            >
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className={tw(styles.td, tdClassName)}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
          {paddingBottom > 0 && (
            <tr aria-hidden="true">
              <td
                colSpan={columns.length}
                className="p-0"
                style={{ height: paddingBottom }}
              />
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
